import { HTMLAttributes } from "react"

import { styled, StyleProps } from "../styles"
import { useTabs } from "./Root"

export interface TabProps extends HTMLAttributes<HTMLElement> {
  /* A unique value that associates the tab with a panel. */
  value: string
  disabled?: boolean
  children: React.ReactNode
  dataTestId?: string
}

export const Tab = ({ value, children, disabled, dataTestId }: TabProps) => {
  const { state, dispatch } = useTabs(`Tab`)

  const isActive = value === state.selectedTab

  return (
    <StyledTab
      data-testid={dataTestId}
      role="tab"
      disabled={disabled}
      active={isActive}
      onClick={() => !isActive && dispatch({ type: `selectTab`, tab: value })}
    >
      {children}
    </StyledTab>
  )
}

Tab.defaultProps = {
  disabled: false,
  dataTestId: undefined,
}

type StyledTabProps = {
  active?: boolean
  disabled?: boolean
}

const StyledTab = styled.div<StyledTabProps & HTMLAttributes<HTMLDivElement>>((props: StyledTabProps & StyleProps) => ({
  boxSizing: `border-box`,
  display: `inline-flex`,
  textTransform: `uppercase`,
  flexShrink: 0,
  padding: props.theme.spacing(3, 6),
  fontWeight: props.theme.fonts.body.fontWeight,
  border: `none`,
  borderRadius: 3,
  userSelect: `none`,
  lineHeight: 1,
  fontSize: props.theme.fontSize.caption,
  cursor: props.active ? `default` : `pointer`,
  backgroundColor: props.active ? props.theme.palette.coal.dark : `transparent`,
  color: props.active ? props.theme.palette.white : `inherit`,
  opacity: props.disabled ? 0.5 : 1,
}))
